import { extractLocalPaymentData, sanitizeProviders } from "../helpers";
import { sanitizePaymentMethodsBrands } from "./sanitizePaymentMethods";
import { ProviderCode } from "../enums";
import type {
  FetchDealerDataResults,
  DealerByIdState,
  MerchantInfoApiResponse,
  PaymentMethodsApiResponse,
} from "../types";
import type { Provider } from "../components/DealerAcquirersTable/types";
import type { DealerProps } from "../../../store/modules/DealerCare/types.store";

export function processDealerInfoResult(
  result: FetchDealerDataResults[0],
): DealerProps | null {
  return result.data.data;
}

export function processProvidersResult(
  result: FetchDealerDataResults[1],
): Provider[] {
  return result.data || [];
}

export function processAvailableProvidersResult(
  result: FetchDealerDataResults[2],
): ReturnType<typeof sanitizeProviders> {
  const allProviders = result.data || [];
  return sanitizeProviders(allProviders as any);
}

export function processMerchantInfoResult(
  result: FetchDealerDataResults[3],
): MerchantInfoApiResponse["data"] | null {
  return result.data;
}

export function processPaymentMethodsResult(
  result: FetchDealerDataResults[4],
): PaymentMethodsApiResponse["data"] {
  const data = result.data || [];
  return sanitizePaymentMethodsBrands(data);
}

export function extractLocalPaymentFromProviders(
  providers: Provider[],
): ReturnType<typeof extractLocalPaymentData> | null {
  const localPaymentProvider = providers.find(
    (provider) =>
      provider.code?.toLowerCase() === ProviderCode.LocalPayment.toLowerCase(),
  );

  return extractLocalPaymentData(localPaymentProvider);
}

export function processAllApiResults(
  results: FetchDealerDataResults,
): Partial<DealerByIdState> {
  const dealerData = processDealerInfoResult(results[0]);
  const providers = processProvidersResult(results[1]);
  const availableProviders = processAvailableProvidersResult(results[2]);
  const merchantInfo = processMerchantInfoResult(results[3]);
  const localPaymentData = extractLocalPaymentFromProviders(providers);
  const paymentMethods = processPaymentMethodsResult(results[4]);

  return {
    dealerData,
    providers,
    availableProviders,
    merchantInfo,
    localPaymentData,
    paymentMethods,
  };
}
